import type { Building } from "building-types/building/buildingTypes";
import type { FloorAssemblyOverride } from "building-types/floors/floorAssemblyTypes";
import type { FloorSystemExternal } from "building-types/floors/floorSystemTypes";
import type { RWTable, Table } from "common/data/table";
import { createFloorInstances } from "../generationTools/components/componentHorizontalAssemblies";
import { shouldGenerateEntity } from "../utils/entityIsolation";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  doc: { optionSets, instanceOptionSets, floorSystems, floors },
  idToParents,
  componentToTransforms,
  levels,
  mirror,
  mirrorTranslationX,
  optionChoices,
  enabledFeatures,
  isolatedEntity,
}: Building) {
  return {
    docFloorSystems: floorSystems,
    docFloors: floors,
    definitions: { optionSets, instanceOptionSets },
    rawOptionChoices: optionChoices,
    idToParents,
    componentToTransforms,
    levels,
    mirror,
    mirrorTranslationX,
    modelComponents: enabledFeatures.modelComponents,
    isolatedEntity,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  docFloorSystems: "shallow",
  docFloors: "shallow",
  definitions: "shallow",
  rawOptionChoices: "shallow",
  idToParents: "shallow",
  componentToTransforms: "shallow",
  levels: "shallow",
  modelComponents: "shallow",
  isolatedEntity: "shallow",
});

export function generateComponentFloors(input: ReturnType<typeof select>): {
  horizontalAssemblyFloors: Table<FloorAssemblyOverride>;
  horizontalAssemblyFloorSystems: Table<FloorSystemExternal>;
} {
  const { docFloorSystems, docFloors, idToParents, isolatedEntity } = input;

  const archetypeFloors: RWTable<FloorAssemblyOverride> = {};
  const archetypeFloorSystems: RWTable<FloorSystemExternal> = {};

  for (const floorSystemId in docFloorSystems) {
    if (shouldGenerateEntity(floorSystemId, idToParents, isolatedEntity)) {
      archetypeFloorSystems[floorSystemId] = docFloorSystems[floorSystemId];
    }
  }
  for (const floorId in docFloors) {
    const floor = docFloors[floorId];
    if (archetypeFloorSystems[floor.parentId]) {
      archetypeFloors[floorId] = floor;
    }
  }

  if (!input.modelComponents) {
    return {
      horizontalAssemblyFloors: archetypeFloors,
      horizontalAssemblyFloorSystems: archetypeFloorSystems,
    };
  }

  const { instanceFloorSystems, instanceFloors } = createFloorInstances(docFloorSystems, docFloors, input);

  return {
    horizontalAssemblyFloors: { ...archetypeFloors, ...instanceFloors },
    horizontalAssemblyFloorSystems: { ...archetypeFloorSystems, ...instanceFloorSystems },
  };
}

export default memo(select, generateComponentFloors, compare);
